// src/components/reports/PaymentsFilters.jsx
import React, { useState, useEffect } from 'react'
import { ChevronDown, X } from 'lucide-react'
import BranchService from '../../services/branchService'
import AuthService from '../../services/authService'

const PaymentsFilters = ({ onFiltersChange, initialFilters = {} }) => {
  const [branches, setBranches] = useState([])
  const [loadingBranches, setLoadingBranches] = useState(true)
  
  // Get user role and branch
  const user = AuthService.getCurrentUser()
  const isManager = user?.role === 'manager'
  const userBranchId = user?.selectedBranch?._id || user?.branchId || user?.branch?._id
  
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    orderType: '',
    paymentMethod: '',
    branch: isManager ? (userBranchId || '') : '',
    ...initialFilters
  })

  // Load branches from API
  useEffect(() => {
    const loadBranches = async () => {
      try {
        setLoadingBranches(true)
        console.log('🔍 Payments Report: Loading branches')
        
        const branchResponse = await BranchService.getAllBranches()
        console.log('✅ Payments Report: Branches response:', branchResponse)
        
        let branchesData = []
        if (branchResponse && branchResponse.success) {
          if (branchResponse.data && branchResponse.data.branches && Array.isArray(branchResponse.data.branches)) {
            branchesData = branchResponse.data.branches
          } else if (Array.isArray(branchResponse.data)) {
            branchesData = branchResponse.data
          }
        }
        
        // Filter to only show outlet branches (exclude warehouses)
        const outletBranches = branchesData.filter(branch => branch.type === 'outlet' && branch.isActive)
        console.log('🏪 Payments Report: Filtered to active outlets:', outletBranches.length, 'branches')
        setBranches(outletBranches)
        
      } catch (error) {
        console.error('❌ Payments Report: Error loading branches:', error)
      } finally {
        setLoadingBranches(false)
      }
    }
    
    loadBranches()
  }, [])

  // Initialize manager's branch filter on mount
  useEffect(() => {
    if (isManager && userBranchId && onFiltersChange) {
      const initialFilters = {
        ...filters,
        branch: userBranchId
      }
      console.log('🔧 Manager detected - Setting initial branch filter:', userBranchId)
      onFiltersChange(initialFilters)
    }
  }, [isManager, userBranchId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Notify parent whenever filters change
  useEffect(() => {
    console.log('PaymentsFilters - Filter state changed:', filters)
    if (onFiltersChange) {
      onFiltersChange(filters)
    }
  }, [filters, onFiltersChange])

  const handleFilterChange = (field, value) => {
    console.log('PaymentsFilters - Filter changed:', { field, value })
    setFilters(prevFilters => ({
      ...prevFilters,
      [field]: value
    }))
  }

  const handleClearFilters = () => {
    const clearedFilters = {
      fromDate: '',
      toDate: '',
      orderType: '',
      paymentMethod: '',
      branch: isManager ? (userBranchId || '') : ''
    }
    console.log('PaymentsFilters - Clearing all filters')
    setFilters(clearedFilters)
  }

  const orderTypeOptions = [
    { value: '', label: 'All Order Types' },
    { value: 'dine_in', label: 'Dine In' },
    { value: 'take_away', label: 'Take Away' },
    { value: 'delivery', label: 'Delivery' }
  ]

  const paymentMethodOptions = [
    { value: '', label: 'All Payment Methods' },
    { value: 'cash', label: 'Cash' },
    { value: 'card', label: 'Card' },
    { value: 'credit', label: 'Credit' },
    { value: 'redeem', label: 'Points Redeem' },
    { value: 'multiple', label: 'Multiple' }
  ]

  // Check if any filters are active (exclude manager's locked branch filter)
  const hasActiveFilters = filters.fromDate || filters.toDate || filters.orderType || filters.paymentMethod || (!isManager && filters.branch)

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">Payments Report Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-orange-600 bg-white border border-orange-300 rounded-lg hover:bg-orange-50 hover:text-orange-700 hover:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 transition-colors"
          >

            <span>Clear Filters</span>
          </button>
        )}
      </div>

      {/* Date Range Row */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Date Range
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">From Date</label>
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To Date</label>
            <input
              type="date"
              value={filters.toDate}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Filters Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Branch Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Branch
          </label>
          <div className="relative">
            {isManager ? (
              // Manager: Read-only branch display
              <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-gray-700 font-medium">
                {branches.find(b => b._id === userBranchId)?.name || 'My Branch'}
              </div>
            ) : (
              // Admin: Branch dropdown
              <>
                <select
                  value={filters.branch || ''}
                  onChange={(e) => handleFilterChange('branch', e.target.value)}
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
                  disabled={loadingBranches}
                >
                  <option value="">All Branches</option>
                  {branches.map((branch) => (
                    <option key={branch._id} value={branch._id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </>
            )}
          </div>
          {loadingBranches && !isManager && <p className="text-xs text-gray-500 mt-1">Loading branches...</p>}
        </div>
        
        {/* Order Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Order Type
          </label>
          <div className="relative">
            <select
              value={filters.orderType}
              onChange={(e) => handleFilterChange('orderType', e.target.value)}
              className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
            >
              {orderTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Payment Method */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Payment Method
          </label>
          <div className="relative">
            <select
              value={filters.paymentMethod}
              onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
              className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
            >
              {paymentMethodOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Current Filters Display */}
      <div className="mt-4 text-xs text-gray-500">
        Active filters: {Object.entries(filters).filter(([, value]) => value !== '').length} of 5
      </div>
    </div>
  )
}

export default PaymentsFilters